/**
 * Version Check — detect new frontend deploy & prompt user to refresh.
 *
 * Strategi:
 * 1. Saat app mount, fetch `/version.txt?ts={now}` (cache-busting query).
 *    Simpan sebagai "initial version".
 * 2. Setiap N detik (default 5 menit), fetch lagi `/version.txt`.
 * 3. Kalau berbeda dari initial → panggil `onUpdate()` (biasanya tampilkan
 *    toast "Versi baru tersedia").
 *
 * `version.txt` harus berisi 1 baris ASCII (timestamp, commit hash, atau
 * build-id apapun). File ini di-generate saat `yarn build` (prebuild script).
 */
const VERSION_URL = "/version.txt";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

let _initialVersion = null;
let _timer = null;

async function _fetchVersion() {
  try {
    const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-store" },
    });
    if (!res.ok) return null;
    const txt = (await res.text()).trim();
    if (!txt) return null;
    // Defensive: kalau respon HTML (404 page kembali ke index.html via SPA fallback)
    // bukan version content yang valid.
    if (txt.toLowerCase().startsWith("<!doctype") || txt.toLowerCase().startsWith("<html")) {
      return null;
    }
    return txt;
  } catch (_) {
    return null;
  }
}

/**
 * Initialize version check. Pass a callback yang akan dipanggil saat versi
 * baru terdeteksi. Callback menerima `{current, next}`.
 *
 * Return cleanup function (untuk dipanggil saat unmount).
 */
export function initVersionCheck(onUpdate) {
  if (typeof window === "undefined") return () => {};

  _fetchVersion().then((v) => {
    if (v) {
      _initialVersion = v;
      console.info(`[versionCheck] frontend build version: ${v}`);
    }
  });

  const tick = async () => {
    const cur = await _fetchVersion();
    if (cur && _initialVersion && cur !== _initialVersion) {
      try { onUpdate?.({ current: _initialVersion, next: cur }); } catch (_) { /* ignore */ }
    }
  };

  _timer = window.setInterval(tick, POLL_INTERVAL_MS);
  // Juga cek saat tab kembali fokus (lebih responsif setelah deploy)
  const onVis = () => { if (document.visibilityState === "visible") tick(); };
  document.addEventListener("visibilitychange", onVis);

  return () => {
    if (_timer) { window.clearInterval(_timer); _timer = null; }
    document.removeEventListener("visibilitychange", onVis);
  };
}

export function getCurrentVersion() {
  return _initialVersion;
}
